import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import { ALBERTA_RESTAURANTS } from '../data/restaurants';
import { FOOD_TRAILS } from '../data/foodTrails';

export default function ProfileView({ onBack, onOpenItinerary, onShowPremiumModal }) {
  const { user, logout, isAuthenticated, refresh } = useAuth();
  const [tab, setTab] = useState('trips'); // trips | trails | favorites | account
  const [trips, setTrips] = useState([]);
  const [favs, setFavs] = useState([]);
  const [savedTrails, setSavedTrails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const [t, f, st] = await Promise.all([
          apiFetch('/api/itineraries'),
          apiFetch('/api/favorites'),
          apiFetch('/api/saved-trails'),
        ]);
        setTrips(t || []);
        setFavs(f || []);
        setSavedTrails(st || []);
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated]);

  const deleteTrip = async (id) => {
    try {
      await apiFetch(`/api/itineraries/${id}`, { method: 'DELETE' });
      setTrips((t) => t.filter((x) => x.id !== id));
    } catch (e) { setErr(e.message); }
  };

  const removeTrail = async (tid) => {
    try {
      await apiFetch(`/api/saved-trails/${tid}`, { method: 'DELETE' });
      setSavedTrails((s) => s.filter((x) => x.trail_id !== tid));
    } catch (e) { setErr(e.message); }
  };

  const trailObjs = savedTrails
    .map((s) => FOOD_TRAILS.find((t) => t.id === s.trail_id))
    .filter(Boolean);

  const unfavorite = async (rid) => {
    try {
      await apiFetch(`/api/favorites/${rid}`, { method: 'DELETE' });
      setFavs((f) => f.filter((x) => x.restaurant_id !== rid));
    } catch (e) { setErr(e.message); }
  };

  const favRestaurants = favs
    .map((f) => ALBERTA_RESTAURANTS.find((r) => r.id === f.restaurant_id))
    .filter(Boolean);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="text-xl font-bold text-gray-900">Culinera</span>
          </div>
          <button data-testid="profile-back-btn" onClick={onBack} className="text-teal-600 hover:text-teal-700 font-semibold">← Back to Home</button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* User card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-600 to-cyan-600 text-white font-bold text-2xl flex items-center justify-center">
              {user.name?.[0]?.toUpperCase() || user.email[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <div className="text-lg font-bold text-gray-900" data-testid="profile-name">{user.name}</div>
            <div className="text-sm text-gray-500" data-testid="profile-email">{user.email}</div>
            <div className="mt-1 text-xs">
              {user.is_premium ? (
                <span className="inline-block bg-purple-100 text-purple-800 px-2 py-0.5 rounded">👑 Premium member</span>
              ) : (
                <span className="inline-block bg-teal-100 text-teal-800 px-2 py-0.5 rounded">Free plan</span>
              )}
            </div>
          </div>
          <button
            data-testid="profile-logout-btn"
            onClick={async () => { await logout(); onBack?.(); }}
            className="text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg px-3 py-2"
          >Sign out</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white p-1 rounded-lg shadow-sm mb-6 max-w-md">
          {[
            ['trips', `Saved trips (${trips.length})`],
            ['trails', `Saved trails (${trailObjs.length})`],
            ['favorites', `Favorites (${favRestaurants.length})`],
            ['account', 'Account'],
          ].map(([id, label]) => (
            <button
              key={id}
              data-testid={`profile-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-semibold transition-colors ${tab === id ? 'bg-teal-600 text-white' : 'text-gray-600 hover:text-teal-600'}`}
            >{label}</button>
          ))}
        </div>

        {err && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">{err}</div>}
        {loading && <div className="text-center text-gray-500">Loading…</div>}

        {tab === 'trips' && !loading && (
          trips.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <p className="text-gray-700 mb-3">No saved trips yet.</p>
              <p className="text-sm text-gray-500">Plan a trip from the home page or spin the wheel for a surprise itinerary — tap <strong>Save to my profile</strong> and it will appear here.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {trips.map((t) => (
                <li key={t.id} data-testid={`saved-trip-${t.id}`} className="bg-white rounded-lg shadow-sm p-4 flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{t.title}</div>
                    <div className="text-xs text-gray-500">📍 {t.startingLocation} · 🗓 {t.tripDuration} · {new Date(t.created_at).toLocaleDateString()}</div>
                    <div className="text-xs text-gray-600 mt-1">{t.restaurants?.length || 0} restaurants</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid={`view-trip-${t.id}`}
                      onClick={() => onOpenItinerary?.(t)}
                      className="bg-teal-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-teal-700"
                    >View</button>
                    <button
                      data-testid={`delete-trip-${t.id}`}
                      onClick={() => deleteTrip(t.id)}
                      className="text-sm text-gray-600 hover:text-red-600 border border-gray-300 rounded-lg px-3 py-2"
                    >Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          )
        )}

        {tab === 'trails' && !loading && (
          trailObjs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <p className="text-gray-700 mb-3">No saved food trails yet.</p>
              <p className="text-sm text-gray-500">Open any <strong>Food Trail</strong> from the directory and tap <strong>Save trail</strong> — it'll show up here for your next trip.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {trailObjs.map((t) => (
                <div key={t.id} data-testid={`saved-trail-${t.id}`} className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex justify-between items-start mb-1">
                    <div className="font-semibold text-gray-900">{t.title}</div>
                    <span className="text-xs bg-teal-100 text-teal-800 px-2 py-0.5 rounded">{t.city}</span>
                  </div>
                  <div className="text-xs text-gray-500 mb-2">{t.tagline} · {t.stops.length} stops</div>
                  <div className="flex gap-2">
                    <a
                      href={`/trails/${t.id}`}
                      data-testid={`open-saved-trail-${t.id}`}
                      className="bg-teal-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-teal-700"
                    >View trail</a>
                    <button
                      onClick={() => removeTrail(t.id)}
                      className="text-sm text-gray-600 hover:text-red-600 border border-gray-300 rounded-lg px-3 py-2"
                    >Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === 'favorites' && !loading && (          favRestaurants.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <p className="text-gray-700 mb-3">No favorites yet.</p>
              <p className="text-sm text-gray-500">Tap the ♥ on any restaurant card in the directory and we'll save it here.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {favRestaurants.map((r) => (
                <div key={r.id} data-testid={`fav-${r.id}`} className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex justify-between items-start mb-1">
                    <div className="font-semibold">{r.name}</div>
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">{r.cuisine}</span>
                  </div>
                  <div className="text-xs text-gray-500 mb-2">📍 {r.city} · {r.price}</div>
                  <p className="text-sm text-gray-700 line-clamp-2">{r.description}</p>
                  <button
                    onClick={() => unfavorite(r.id)}
                    className="mt-3 text-xs text-gray-500 hover:text-red-600"
                  >Remove favorite</button>
                </div>
              ))}
            </div>
          )
        )}

        {tab === 'account' && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Plan</h3>
              {user.is_premium ? (
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">👑 Culinera Premium · $9.99/month</span>
                  <a
                    data-testid="billing-portal-link"
                    href="https://billing.stripe.com/p/login/test_dR6eYX8Hn3jz4o8cMM"
                    target="_blank"
                    rel="noreferrer"
                    className="text-teal-600 hover:underline text-sm"
                  >Manage billing →</a>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Free plan</span>
                  <button
                    data-testid="upgrade-from-profile"
                    onClick={() => onShowPremiumModal?.()}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700"
                  >Upgrade to Premium</button>
                </div>
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Sign-in method</h3>
              <p className="text-sm text-gray-600">{user.auth_provider === 'google' ? 'Continue with Google' : 'Email & password'}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Support</h3>
              <p className="text-sm text-gray-600">Questions, cancellations, billing — email <a className="text-teal-600 hover:underline" href="mailto:hello@culinera.ca">hello@culinera.ca</a>.</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
